import { currentDivisionId } from 'common/src/stores/useFiltersStore';
import {
  getComparativeIndustryClients,
  getSettlementsByExporterId,
  getSettlementsFobVsExporters,
} from 'common/src/api/settlements';
import { Settlement } from 'common/src/utils/types';
import { useEffect, useMemo, useState, useRef } from 'react';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import {
  Select,
  SelectItem,
  Button,
  Card,
  CardBody,
  Checkbox,
  Divider,
  Switch,
} from '@nextui-org/react';
import {
  ComparativeClientsUtility,
  GroupedSettlement,
  MainClientDetailWithComparable,
  VarietyStats,
} from '../../../services/comparative-clientes/comparative-clients.utility';
import {
  ComparativeIndustryUtility,
  groupSettlementsByShipTrip,
  getShipTripIds,
  IndustryData,
  FiveNumberSummary,
  getQuartile,
  QuartileDistribution,
} from '../../../services/comparative-industry/comparative-industry.utility';
import { Icon } from '@iconify/react';
import { formatMoneyUSD } from '../../../utils/functions';
import ShipmentBarChart from '../../../components/specifics/settlements-client-comparision/ShipmentBarChart';
import VarietyComparisionBarChart from '../../../components/specifics/settlements-client-comparision/VarietyComparisionBarChart';
import CaliberComparisionBarChart from '../../../components/specifics/settlements-client-comparision/CaliberComparisionBarChart';
import IndustryDimensionAnalysis, {
  IndustryDimensionRow,
} from '../../../components/specifics/settlements-client-comparision/IndustryDimensionAnalysis';
import { logUserAction } from '../../../services/actionsService';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import InformationTooltip from '../../../../../common/src/components/ui/InformationTooltip';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import { createClaim, createClaimDetails } from 'common/src/api/claims';
import { toast } from 'react-toastify';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getBrandAssets } from 'common/src/utils/branding';
import MultiSelectWithActions from '../../../components/common/MultiSelectWithActions';
import ReactApexChart from 'react-apexcharts';
import {
  Alert,
  LoadingState,
  PageHeader,
  PageLayout,
} from '../../../components/ui';

const ALL_CLIENTS = '__all__';

interface SummaryStatistics {
  totalFobDifference: number;
  perKiloDifference: number;
  varietyStats: VarietyStats[];
  mainClient: {
    totalKilos: number;
  };
  comparablePercentage: number;
}

const SettlementsComparativeIndustry = () => {
  const { t } = useTranslation('settlementsComparative');
  const { session, userProfile, darkMode } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();

  const [allSettlements, setAllSettlements] = useState<Settlement[]>([]);
  const [availableClients, setAvailableClients] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [selectedResultClients, setSelectedResultClients] = useState<string[]>(
    []
  );
  const [methodology, setMethodology] = useState<'by_volume' | 'by_exporter'>(
    'by_exporter'
  );
  const [sinisterFilter, setSinisterFilter] = useState<
    'all' | 'sinister' | 'non_sinister'
  >('all');
  const [priceType, setPriceType] = useState<'FOB' | 'CIF'>('FOB');
  const [includeRevenues, setIncludeRevenues] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [summaryStatistics, setSummaryStatistics] =
    useState<SummaryStatistics | null>(null);
  const [shipmentData, setShipmentData] = useState<{
    byShips: { [key: string]: GroupedSettlement };
    byWeeks: { [key: string]: GroupedSettlement };
  } | null>(null);
  const [mainClientDetails, setMainClientDetails] = useState<
    MainClientDetailWithComparable[]
  >([]);
  // Raw data stored to recompute shipmentData when comparisonTarget changes
  const [rawShipGroups, setRawShipGroups] = useState<Record<
    string,
    Settlement[]
  > | null>(null);
  const [rawIndustryDataByGroup, setRawIndustryDataByGroup] = useState<Record<
    string,
    IndustryData
  > | null>(null);
  const [isOptionsChanged, setIsOptionsChanged] = useState(false);
  const [isSavingClaim, setIsSavingClaim] = useState(false);
  const [claimDescription, setClaimDescription] = useState('');
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [diffContextComparableOnly, setDiffContextComparableOnly] =
    useState(false);
  const [selectedVarieties, setSelectedVarieties] = useState<string[]>([]);
  const [selectedCalibers, setSelectedCalibers] = useState<string[]>([]);
  const [selectedShipTrips, setSelectedShipTrips] = useState<string[]>([]);
  const [selectedDestinationCountries, setSelectedDestinationCountries] =
    useState<string[]>([]);
  const [comparisonTarget, setComparisonTarget] = useState<
    'median' | 'q1' | 'q3'
  >('median');
  const actionsCardRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Fetch the small client-list payload independently from settlement details.
  useEffect(() => {
    let isCurrent = true;

    const fetchClients = async () => {
      if (!userProfile?.exporter_id || !selectedSpecies?.id) return;

      setIsLoadingClients(true);
      try {
        const clients = await getComparativeIndustryClients(
          +userProfile.exporter_id,
          selectedSeason,
          selectedSpecies?.id,
          sinisterFilter,
          priceType,
          currentDivisionId()
        );
        if (isCurrent) setAvailableClients(clients);
      } finally {
        if (isCurrent) setIsLoadingClients(false);
      }
    };

    fetchClients();
    return () => {
      isCurrent = false;
    };
  }, [
    selectedSeason,
    sinisterFilter,
    priceType,
    userProfile?.exporter_id,
    selectedSpecies,
  ]);

  // Fetch the full dataset once. Price and sinister filters are applied locally,
  // avoiding a second large request and refetches when those controls change.
  useEffect(() => {
    let isCurrent = true;

    const fetchAllSettlements = async () => {
      if (!userProfile?.exporter_id || !selectedSpecies?.id) return;
      const fetched = await getSettlementsByExporterId(
        +userProfile.exporter_id,
        'approved',
        selectedSeason,
        selectedSpecies?.id,
        'all',
        ['*'],
        currentDivisionId()
      );
      if (isCurrent) setAllSettlements(fetched || []);
    };

    fetchAllSettlements();
    return () => {
      isCurrent = false;
    };
  }, [selectedSeason, userProfile?.exporter_id, selectedSpecies]);

  const settlements = useMemo(() => {
    return allSettlements.filter((settlement) => {
      const hasRequiredPriceData =
        priceType === 'FOB'
          ? settlement.total_freight_usd !== null &&
            settlement.total_freight_usd > 0 &&
            settlement.exchange_rate !== null
          : settlement.exchange_rate !== null;
      const matchesSinisterFilter =
        sinisterFilter === 'all' ||
        (sinisterFilter === 'sinister'
          ? settlement.is_sinister
          : !settlement.is_sinister);
      const shipType = settlement.ship_trip?.ship?.type;

      return (
        hasRequiredPriceData &&
        matchesSinisterFilter &&
        (!shipType || shipType === 'M')
      );
    });
  }, [allSettlements, priceType, sinisterFilter]);

  useEffect(() => {
    logUserAction('view', 'page', 'settlements-comparative-industry');
  }, []);

  // Track option changes
  useEffect(() => {
    if (summaryStatistics) {
      setIsOptionsChanged(true);
    }
  }, [
    selectedClients,
    priceType,
    sinisterFilter,
    methodology,
    includeRevenues,
  ]);

  // Extract unique varieties from settlement details
  const uniqueVarieties = useMemo(() => {
    const varieties = new Set<string>();
    settlements.forEach((s) => {
      s.settlement_details?.forEach((d: any) => {
        if (d.variety) varieties.add(d.variety);
      });
    });
    return [...varieties].sort((a, b) => a.localeCompare(b));
  }, [settlements]);

  const uniqueCalibers = useMemo(() => {
    const calibers = new Set<string>();
    settlements.forEach((settlement) => {
      settlement.settlement_details?.forEach((detail) => {
        if (detail.caliber) calibers.add(detail.caliber);
      });
    });
    return [...calibers].sort((a, b) => a.localeCompare(b));
  }, [settlements]);

  const uniqueDestinationCountries = useMemo(() => {
    const countries = new Set<string>();
    settlements.forEach((settlement) => {
      const country =
        settlement.ship_trip?.market?.country ||
        settlement.market?.country ||
        'Sin País';
      countries.add(country);
    });
    return [...countries].sort((a, b) => a.localeCompare(b));
  }, [settlements]);

  // Extract unique ship trips with ETA and market info
  const uniqueShipTrips = useMemo(() => {
    const tripMap = new Map<string, { id: string; label: string }>();
    settlements.forEach((s) => {
      const trip = s.ship_trip;
      if (!trip) return;
      const tripId = String(trip.id);
      if (tripMap.has(tripId)) return;
      const shipName = trip.ship?.name || 'Sin nave';
      const eta = trip.eta
        ? new Date(trip.eta).toLocaleDateString('es-CL', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : '';
      const market = (trip as any).market?.name || '';
      const parts = [shipName, eta, market].filter(Boolean);
      tripMap.set(tripId, { id: tripId, label: parts.join(' · ') });
    });
    return [...tripMap.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [settlements]);

  // Initialize filters with all selected when lists change
  useEffect(() => {
    setSelectedVarieties(uniqueVarieties);
  }, [uniqueVarieties]);

  useEffect(() => {
    setSelectedCalibers(uniqueCalibers);
  }, [uniqueCalibers]);

  useEffect(() => {
    setSelectedShipTrips(uniqueShipTrips.map((t) => t.id));
  }, [uniqueShipTrips]);

  useEffect(() => {
    setSelectedDestinationCountries(uniqueDestinationCountries);
  }, [uniqueDestinationCountries]);

  const resultClientOptions = useMemo(
    () =>
      selectedClients.includes(ALL_CLIENTS)
        ? availableClients
        : selectedClients,
    [availableClients, selectedClients]
  );

  useEffect(() => {
    setSelectedResultClients(resultClientOptions);
  }, [resultClientOptions]);

  const isComparisonReady = selectedClients.length > 0 && !!methodology;

  // Total difference based on selected comparison target
  const totalDiffForTarget = useMemo(() => {
    if (!mainClientDetails.length) return 0;
    if (comparisonTarget === 'q1') {
      return mainClientDetails.reduce(
        (sum, d) => sum + (d.differenceQ1USD || 0),
        0
      );
    }
    if (comparisonTarget === 'q3') {
      return mainClientDetails.reduce(
        (sum, d) => sum + (d.differenceQ3USD || 0),
        0
      );
    }
    // median — use the existing summaryStatistics value
    return summaryStatistics?.totalFobDifference ?? 0;
  }, [mainClientDetails, comparisonTarget, summaryStatistics]);

  const diffContext = useMemo(() => {
    if (!summaryStatistics || selectedClients.length === 0) return null;

    const diff = totalDiffForTarget;

    let totalFob = 0;
    let totalCommission = 0;

    if (diffContextComparableOnly) {
      // Only comparable details for the selected client
      const comparableDetailIds = new Set(
        mainClientDetails
          .filter((d) => d.hasComparison)
          .map((d) => d.settlement_detail_id)
      );

      settlements
        .filter(
          (settlement) =>
            !!settlement.receiver &&
            selectedResultClients.includes(settlement.receiver)
        )
        .forEach((s) => {
          s.settlement_details?.forEach((d: any) => {
            if (!comparableDetailIds.has(d.id)) return;
            const exchangeRate = d.exchange_rate || 1;
            totalFob += d.usd_total_fob || 0;
            totalCommission += (d.local_total_commision || 0) / exchangeRate;
          });
        });
    } else {
      // ALL settlements for the client in the season/species (no sinister/priceType/maritime filters)
      allSettlements
        .filter(
          (settlement) =>
            !!settlement.receiver &&
            selectedResultClients.includes(settlement.receiver)
        )
        .forEach((s) => {
          s.settlement_details?.forEach((d: any) => {
            const exchangeRate = d.exchange_rate || 1;
            totalFob += d.usd_total_fob || 0;
            totalCommission += (d.local_total_commision || 0) / exchangeRate;
          });
        });
    }

    return {
      pctOfFob: totalFob !== 0 ? (diff / totalFob) * 100 : null,
      pctOfCommission:
        totalCommission !== 0 ? (diff / totalCommission) * 100 : null,
    };
  }, [
    summaryStatistics,
    settlements,
    allSettlements,
    selectedClients,
    selectedResultClients,
    diffContextComparableOnly,
    mainClientDetails,
    totalDiffForTarget,
  ]);

  // % vs target ponderado por valor CIF/FOB
  // score_i = (fobPerKg - target) / target, ponderado por CIF value
  const computeScoreAndQuartiles = (
    details: MainClientDetailWithComparable[],
    groupFn: (d: MainClientDetailWithComparable) => string | null,
    target: 'median' | 'q1' | 'q3'
  ) => {
    const groups: Record<
      string,
      {
        weightedScoreSum: number;
        totalCifWeight: number;
        q1Kilos: number;
        q2Kilos: number;
        q3Kilos: number;
        q4Kilos: number;
        totalKilos: number;
      }
    > = {};

    details.forEach((d) => {
      if (!d.hasComparison || !d.industryFiveNum || !d.comparisonFobPerKg)
        return;
      const key = groupFn(d);
      if (!key) return;
      if (!groups[key])
        groups[key] = {
          weightedScoreSum: 0,
          totalCifWeight: 0,
          q1Kilos: 0,
          q2Kilos: 0,
          q3Kilos: 0,
          q4Kilos: 0,
          totalKilos: 0,
        };

      const targetVal = d.industryFiveNum[target];
      if (targetVal > 0 && d.fob > 0) {
        const score = (d.fobPerKg - targetVal) / targetVal;
        groups[key].weightedScoreSum += score * d.fob;
        groups[key].totalCifWeight += d.fob;
      }

      const quartile = getQuartile(
        d.fobPerKg,
        d.industryFiveNum as FiveNumberSummary
      );
      if (quartile === 1) groups[key].q1Kilos += d.kilos;
      else if (quartile === 2) groups[key].q2Kilos += d.kilos;
      else if (quartile === 3) groups[key].q3Kilos += d.kilos;
      else groups[key].q4Kilos += d.kilos;
      groups[key].totalKilos += d.kilos;
    });

    const scores: Record<string, number> = {};
    const quartiles: Record<string, QuartileDistribution> = {};
    Object.entries(groups).forEach(([k, v]) => {
      if (v.totalCifWeight > 0)
        scores[k] = v.weightedScoreSum / v.totalCifWeight;
      if (v.totalKilos > 0)
        quartiles[k] = {
          q1Kilos: v.q1Kilos,
          q2Kilos: v.q2Kilos,
          q3Kilos: v.q3Kilos,
          q4Kilos: v.q4Kilos,
          totalKilos: v.totalKilos,
        };
    });
    return { scores, quartiles };
  };

  // Global % vs target & quartile distribution
  const globalMetrics = useMemo(() => {
    if (!mainClientDetails.length) return null;
    const result = computeScoreAndQuartiles(
      mainClientDetails,
      () => '__global__',
      comparisonTarget
    );
    return {
      pctVsTarget: result.scores['__global__'] ?? null,
      quartileDist: result.quartiles['__global__'] ?? null,
    };
  }, [mainClientDetails, comparisonTarget]);

  // By Variety
  const metricsByVariety = useMemo(() => {
    if (!mainClientDetails.length) return undefined;
    return computeScoreAndQuartiles(
      mainClientDetails,
      (d) => d.productKey.split('-')[0],
      comparisonTarget
    );
  }, [mainClientDetails, comparisonTarget]);

  // By Caliber
  const metricsByCaliber = useMemo(() => {
    if (!mainClientDetails.length) return undefined;
    return computeScoreAndQuartiles(
      mainClientDetails,
      (d) => d.productKey.split('-')[1],
      comparisonTarget
    );
  }, [mainClientDetails, comparisonTarget]);

  // By Ship
  const metricsByShip = useMemo(() => {
    if (!mainClientDetails.length) return undefined;
    return computeScoreAndQuartiles(
      mainClientDetails,
      (d) => d.groupKey,
      comparisonTarget
    );
  }, [mainClientDetails, comparisonTarget]);

  const metricsByClient = useMemo(() => {
    if (!mainClientDetails.length) return undefined;
    return computeScoreAndQuartiles(
      mainClientDetails,
      (detail) => detail.client || t('Sin Cliente'),
      comparisonTarget
    );
  }, [mainClientDetails, comparisonTarget, t]);

  const metricsByDestinationCountry = useMemo(() => {
    if (!mainClientDetails.length) return undefined;
    return computeScoreAndQuartiles(
      mainClientDetails,
      (detail) => detail.destinationCountry || t('Sin País'),
      comparisonTarget
    );
  }, [mainClientDetails, comparisonTarget, t]);

  // Helper: % of kilos above the selected comparison target
  const kilosAboveTarget = (qd: QuartileDistribution): number => {
    if (qd.totalKilos === 0) return 0;
    if (comparisonTarget === 'q1')
      return Math.round(
        ((qd.q2Kilos + qd.q3Kilos + qd.q4Kilos) / qd.totalKilos) * 100
      );
    if (comparisonTarget === 'q3')
      return Math.round((qd.q4Kilos / qd.totalKilos) * 100);
    // median
    return Math.round(((qd.q3Kilos + qd.q4Kilos) / qd.totalKilos) * 100);
  };

  const targetLabel =
    comparisonTarget === 'median'
      ? 'Mediana'
      : comparisonTarget === 'q1'
        ? 'Q1'
        : 'Q3';

  const buildDimensionRows = (
    groupFn: (detail: MainClientDetailWithComparable) => string,
    scores?: Record<string, number>,
    quartiles?: Record<string, QuartileDistribution>
  ): IndustryDimensionRow[] => {
    const rows: Record<string, IndustryDimensionRow> = {};
    const comparisonKeysByDimension: Record<string, Set<string>> = {};

    mainClientDetails.forEach((detail) => {
      if (!detail.hasComparison) return;
      const key = groupFn(detail);
      if (!rows[key]) {
        rows[key] = {
          key,
          kilos: 0,
          othersKilos: 0,
          totalValue: 0,
          totalDifference: 0,
          pctVsTarget: null,
          kilosAboveTargetPct: null,
          q1Pct: null,
          q2Pct: null,
          q3Pct: null,
          q4Pct: null,
        };
      }

      rows[key].kilos += detail.kilos;
      rows[key].totalValue += detail.fob;
      rows[key].totalDifference +=
        comparisonTarget === 'q1'
          ? detail.differenceQ1USD || 0
          : comparisonTarget === 'q3'
            ? detail.differenceQ3USD || 0
            : detail.differenceUSD || 0;

      if (!comparisonKeysByDimension[key]) {
        comparisonKeysByDimension[key] = new Set<string>();
      }
      const comparisonKey = `${detail.groupKey}::${detail.productKey}`;
      if (!comparisonKeysByDimension[key].has(comparisonKey)) {
        comparisonKeysByDimension[key].add(comparisonKey);
        rows[key].othersKilos += detail.comparableKilos;
      }
    });

    return Object.values(rows).map((row) => {
      const quartileDistribution = quartiles?.[row.key];
      return {
        ...row,
        pctVsTarget: scores?.[row.key] ?? null,
        kilosAboveTargetPct: quartileDistribution
          ? kilosAboveTarget(quartileDistribution)
          : null,
        q1Pct: quartileDistribution
          ? Math.round(
              (quartileDistribution.q1Kilos / quartileDistribution.totalKilos) *
                100
            )
          : null,
        q2Pct: quartileDistribution
          ? Math.round(
              (quartileDistribution.q2Kilos / quartileDistribution.totalKilos) *
                100
            )
          : null,
        q3Pct: quartileDistribution
          ? Math.round(
              (quartileDistribution.q3Kilos / quartileDistribution.totalKilos) *
                100
            )
          : null,
        q4Pct: quartileDistribution
          ? Math.round(
              (quartileDistribution.q4Kilos / quartileDistribution.totalKilos) *
                100
            )
          : null,
      };
    });
  };

  const clientAnalysisRows = useMemo(
    () =>
      buildDimensionRows(
        (detail) => detail.client || t('Sin Cliente'),
        metricsByClient?.scores,
        metricsByClient?.quartiles
      ),
    [mainClientDetails, comparisonTarget, metricsByClient, t]
  );

  const destinationCountryAnalysisRows = useMemo(
    () =>
      buildDimensionRows(
        (detail) => detail.destinationCountry || t('Sin País'),
        metricsByDestinationCountry?.scores,
        metricsByDestinationCountry?.quartiles
      ),
    [mainClientDetails, comparisonTarget, metricsByDestinationCountry, t]
  );

  const handleGenerateComparison = async () => {
    if (!isComparisonReady || !session) return;

    setIsLoading(true);
    setIsOptionsChanged(false);

    try {
      const incoterm = priceType === 'FOB' ? 'fob' : 'cif';

      // 1. Filter settlements for the selected client (NO variety/trip filtering here)
      const clientSettlements = selectedClients.includes(ALL_CLIENTS)
        ? settlements
        : settlements.filter(
            (settlement) =>
              !!settlement.receiver &&
              selectedClients.includes(settlement.receiver)
          );

      if (clientSettlements.length === 0) {
        setSummaryStatistics(null);
        setShipmentData(null);
        setMainClientDetails([]);
        setRawShipGroups(null);
        setRawIndustryDataByGroup(null);
        setIsLoading(false);
        return;
      }

      // 2. Group ALL client settlements by ship trip (unfiltered)
      const shipGroups = groupSettlementsByShipTrip(clientSettlements);

      // 3. Fetch industry data per group in parallel
      const groupEntries = Object.entries(shipGroups);
      const industryDataByGroup: Record<string, IndustryData> = {};

      await Promise.all(
        groupEntries.map(async ([groupKey, groupSettlements]) => {
          const tripIds = getShipTripIds(groupSettlements);
          if (tripIds.length === 0) return;

          const data = await getSettlementsFobVsExporters(
            session,
            methodology,
            selectedSeason,
            selectedSpecies?.id,
            sinisterFilter,
            'none',
            undefined,
            undefined,
            'eta',
            incoterm,
            undefined,
            undefined,
            tripIds
          );

          if (data) {
            industryDataByGroup[groupKey] = data as unknown as IndustryData;
          }
        })
      );

      // 4. Store unfiltered raw data for client-side recomputation
      setRawShipGroups(shipGroups);
      setRawIndustryDataByGroup(industryDataByGroup);

      // 5. Apply variety/trip filters and build results
      recomputeFromRaw(shipGroups, industryDataByGroup);

      setTimeout(() => {
        if (actionsCardRef.current) {
          actionsCardRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    } catch (error) {
      console.error('Error generating industry comparison:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Apply the selected analysis filters to the cached raw data.
  const recomputeFromRaw = (
    shipGroups: Record<string, Settlement[]>,
    industryDataByGroup: Record<string, IndustryData>
  ) => {
    // Filter by selected ship trips
    let filteredGroups = { ...shipGroups };

    // Filter clients inside the initially selected comparison universe.
    if (selectedResultClients.length < resultClientOptions.length) {
      const clientSet = new Set(selectedResultClients);
      const filtered: Record<string, Settlement[]> = {};
      Object.entries(filteredGroups).forEach(([key, setts]) => {
        const kept = setts.filter(
          (settlement) =>
            !!settlement.receiver && clientSet.has(settlement.receiver)
        );
        if (kept.length > 0) filtered[key] = kept;
      });
      filteredGroups = filtered;
    }

    if (selectedShipTrips.length < uniqueShipTrips.length) {
      const tripIdSet = new Set(selectedShipTrips);
      const filtered: Record<string, Settlement[]> = {};
      Object.entries(filteredGroups).forEach(([key, setts]) => {
        const kept = setts.filter((s) =>
          tripIdSet.has(String(s.ship_trip?.id || ''))
        );
        if (kept.length > 0) filtered[key] = kept;
      });
      filteredGroups = filtered;
    }

    // Filter by destination country
    if (
      selectedDestinationCountries.length < uniqueDestinationCountries.length
    ) {
      const destinationCountrySet = new Set(selectedDestinationCountries);
      const filtered: Record<string, Settlement[]> = {};
      Object.entries(filteredGroups).forEach(([key, setts]) => {
        const kept = setts.filter((settlement) => {
          const country =
            settlement.ship_trip?.market?.country ||
            settlement.market?.country ||
            'Sin País';
          return destinationCountrySet.has(country);
        });
        if (kept.length > 0) filtered[key] = kept;
      });
      filteredGroups = filtered;
    }

    // Filter settlement_details by selected varieties and calibers
    // Preserve _originalTotalKilos so getDetailValue computes freight correctly
    if (
      selectedVarieties.length < uniqueVarieties.length ||
      selectedCalibers.length < uniqueCalibers.length
    ) {
      const varietySet = new Set(selectedVarieties);
      const caliberSet = new Set(selectedCalibers);
      const filtered: Record<string, Settlement[]> = {};
      Object.entries(filteredGroups).forEach(([key, setts]) => {
        const kept = setts
          .map((s) => {
            const originalTotalKilos =
              s.settlement_details?.reduce(
                (acc: number, d: any) => acc + (d.kilos || 0),
                0
              ) || 0;
            return {
              ...s,
              _originalTotalKilos: originalTotalKilos,
              settlement_details: s.settlement_details?.filter(
                (d: any) =>
                  varietySet.has(d.variety) && caliberSet.has(d.caliber)
              ),
            };
          })
          .filter(
            (s) => s.settlement_details && s.settlement_details.length > 0
          );
        if (kept.length > 0) filtered[key] = kept as Settlement[];
      });
      filteredGroups = filtered;
    }

    // Filter industry data to only include groups that survived
    const filteredIndustry: Record<string, IndustryData> = {};
    Object.keys(filteredGroups).forEach((key) => {
      if (industryDataByGroup[key]) {
        filteredIndustry[key] = industryDataByGroup[key];
      }
    });

    if (Object.keys(filteredGroups).length === 0) {
      setSummaryStatistics({
        totalFobDifference: 0,
        perKiloDifference: 0,
        varietyStats: [],
        mainClient: { totalKilos: 0 },
        comparablePercentage: 0,
      });
      setShipmentData({ byShips: {}, byWeeks: {} });
      setMainClientDetails([]);
      return;
    }

    const grouped =
      ComparativeIndustryUtility.buildGroupedSettlementsWithIndustry(
        filteredGroups,
        selectedResultClients,
        filteredIndustry,
        priceType,
        comparisonTarget,
        includeRevenues
      );

    const stats = ComparativeClientsUtility.calculateSummaryStatistics(
      grouped.byShips
    );

    setSummaryStatistics(stats as SummaryStatistics);
    setShipmentData({
      byShips: grouped.byShips,
      byWeeks: grouped.byShips,
    });
    setMainClientDetails(grouped.mainClientDetails);
  };

  // Recompute when a local parameter changes (no refetch).
  useEffect(() => {
    if (
      !rawShipGroups ||
      !rawIndustryDataByGroup ||
      selectedClients.length === 0
    )
      return;
    recomputeFromRaw(rawShipGroups, rawIndustryDataByGroup);
  }, [
    comparisonTarget,
    selectedVarieties,
    selectedCalibers,
    selectedShipTrips,
    selectedDestinationCountries,
    selectedResultClients,
  ]);

  const loadImageAsBase64 = (src: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  };

  const formatGroupLabel = (key: string): string => {
    if (key.startsWith('AIR-')) {
      const week = key.split('-W')[1];
      return `Air - Week ${week}`;
    } else if (key.startsWith('GROUND-')) {
      const week = key.split('-W')[1];
      return `Ground - Week ${week}`;
    } else if (key.startsWith('SHIP-')) {
      const parts = key.split('-');
      return parts.length > 1 ? parts[1].replace(/_/g, ' ') : key;
    }
    return key;
  };

  const exportToPDF = async () => {
    if (!shipmentData || !summaryStatistics) return;

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 18;
      const contentWidth = pageWidth - margin * 2;
      const centerX = pageWidth / 2;

      // Palette
      const cherry = { r: 190, g: 18, b: 60 };
      const charcoal = { r: 24, g: 24, b: 27 };
      const slate = { r: 71, g: 85, b: 105 };
      const muted = { r: 148, g: 163, b: 184 };
      const faintBg = { r: 248, g: 250, b: 252 };
      const borderC = { r: 226, g: 232, b: 240 };
      const green = { r: 22, g: 163, b: 74 };
      const red = { r: 220, g: 38, b: 38 };
      const logoAspect = 1575 / 245;

      const brand = getBrandAssets();
      const [, whiteLogoBase64] = await Promise.all([
        loadImageAsBase64(brand.fullLogoLight),
        loadImageAsBase64(brand.fullLogoDark),
      ]);

      const today = new Date();
      const dateStr = today.toLocaleDateString('en-US', {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      });

      // Helper: draw footer on every page (called after all content)
      const drawFooter = () => {
        const totalPages = doc.getNumberOfPages();
        for (let p = 1; p <= totalPages; p++) {
          doc.setPage(p);
          const footH = 10;
          doc.setFillColor(charcoal.r, charcoal.g, charcoal.b);
          doc.rect(0, pageHeight - footH, pageWidth, footH, 'F');
          // Cherry accent line on top of footer
          doc.setFillColor(cherry.r, cherry.g, cherry.b);
          doc.rect(0, pageHeight - footH, pageWidth, 1, 'F');
          if (whiteLogoBase64) {
            const fH = 3.5;
            doc.addImage(
              whiteLogoBase64,
              'PNG',
              margin,
              pageHeight - footH / 2 - fH / 2,
              fH * logoAspect,
              fH
            );
          }
          doc.setFontSize(6);
          doc.setTextColor(160, 160, 160);
          doc.setFont('helvetica', 'normal');
          doc.text(
            `Confidential  ·  ${dateStr}  ·  Page ${p}/${totalPages}`,
            pageWidth - margin,
            pageHeight - footH / 2 + 1.5,
            { align: 'right' }
          );
        }
      };

      // ════════════════════════════════════════
      // HEADER — dark bar + cherry accent
      // ════════════════════════════════════════
      const headerH = 26;
      doc.setFillColor(charcoal.r, charcoal.g, charcoal.b);
      doc.rect(0, 0, pageWidth, headerH, 'F');
      doc.setFillColor(cherry.r, cherry.g, cherry.b);
      doc.rect(0, headerH, pageWidth, 1.2, 'F');

      if (whiteLogoBase64) {
        const lH = 6.5;
        doc.addImage(
          whiteLogoBase64,
          'PNG',
          margin,
          (headerH - lH) / 2,
          lH * logoAspect,
          lH
        );
      }

      const certNum = `N° ${Date.now().toString().slice(-8)}`;
      doc.setFontSize(7);
      doc.setTextColor(130, 130, 130);
      doc.setFont('helvetica', 'normal');
      doc.text(certNum, pageWidth - margin, headerH / 2 + 1, {
        align: 'right',
      });

      // ════════════════════════════════════════
      // TITLE
      // ════════════════════════════════════════
      let y = headerH + 13;
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(charcoal.r, charcoal.g, charcoal.b);
      doc.text('C E R T I F I C A T E', centerX, y, { align: 'center' });

      y += 8;
      doc.setFontSize(10.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(cherry.r, cherry.g, cherry.b);
      doc.text('Industry Comparison Report', centerX, y, { align: 'center' });

      // Double decorative rule
      y += 5;
      const ruleL = margin + 35;
      const ruleR = pageWidth - margin - 35;
      doc.setDrawColor(borderC.r, borderC.g, borderC.b);
      doc.setLineWidth(0.35);
      doc.line(ruleL, y, ruleR, y);
      doc.setLineWidth(0.12);
      doc.line(ruleL, y + 1.4, ruleR, y + 1.4);

      // ════════════════════════════════════════
      // INFO PANEL
      // ════════════════════════════════════════
      y += 7;
      const panelH = 62;
      doc.setFillColor(faintBg.r, faintBg.g, faintBg.b);
      doc.setDrawColor(borderC.r, borderC.g, borderC.b);
      doc.setLineWidth(0.2);
      doc.roundedRect(margin, y, contentWidth, panelH, 2.5, 2.5, 'FD');

      const sinisterLabel =
        sinisterFilter === 'all'
          ? 'All'
          : sinisterFilter === 'sinister'
            ? 'Claims Only'
            : 'No Claims';

      const targetLabel =
        comparisonTarget === 'median'
          ? 'Median'
          : comparisonTarget === 'q1'
            ? 'Q1'
            : 'Q3';
      const varietyFilterLabel =
        selectedVarieties.length < uniqueVarieties.length
          ? selectedVarieties.length > 0
            ? selectedVarieties.join(', ')
            : 'None'
          : 'All';
      const caliberFilterLabel =
        selectedCalibers.length < uniqueCalibers.length
          ? selectedCalibers.length > 0
            ? selectedCalibers.join(', ')
            : 'None'
          : 'All';
      const countryFilterLabel =
        selectedDestinationCountries.length <
        uniqueDestinationCountries.length
          ? selectedDestinationCountries.length > 0
            ? selectedDestinationCountries.join(', ')
            : 'None'
          : 'All';
      const resultClientFilterLabel =
        selectedResultClients.length === 0
          ? 'None'
          : selectedClients.includes(ALL_CLIENTS) &&
              selectedResultClients.length === availableClients.length
            ? 'All'
            : selectedResultClients.join(', ');
      const tripFilterLabel =
        selectedShipTrips.length < uniqueShipTrips.length
          ? selectedShipTrips.length > 0
            ? uniqueShipTrips
                .filter((t) => selectedShipTrips.includes(t.id))
                .map((t) => t.label)
                .join(', ')
            : 'None'
          : 'All';

      const compactFilterLabel = (value: string, selectedCount: number) =>
        value.length > 40 ? `${selectedCount} selected` : value;
      const sanitizePdfText = (value: string) =>
        value
          .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
          .trim();

      const infoItems = [
        [
          { label: 'EXPORTER', value: userProfile?.exporter?.name || '-' },
          {
            label: 'CLIENT',
            value: compactFilterLabel(
              resultClientFilterLabel,
              selectedResultClients.length
            ),
          },
          { label: 'SEASON', value: selectedSeason || '-' },
        ],
        [
          {
            label: 'SPECIES',
            value: sanitizePdfText(selectedSpecies?.name || '-'),
          },
          { label: 'INCOTERM', value: priceType },
          { label: 'CLAIMS FILTER', value: sinisterLabel },
        ],
        [
          {
            label: 'ALGORITHM',
            value: methodology === 'by_volume' ? 'By Volume' : 'By Exporter',
          },
          { label: 'COMPARISON TARGET', value: targetLabel },
        ],
        [
          {
            label: 'DEST. COUNTRIES',
            value: compactFilterLabel(
              countryFilterLabel,
              selectedDestinationCountries.length
            ),
          },
          {
            label: 'SHIP TRIPS',
            value: compactFilterLabel(
              tripFilterLabel,
              selectedShipTrips.length
            ),
          },
          {
            label: 'VARIETIES',
            value: compactFilterLabel(
              varietyFilterLabel,
              selectedVarieties.length
            ),
          },
        ],
        [
          {
            label: 'SIZES',
            value: compactFilterLabel(
              caliberFilterLabel,
              selectedCalibers.length
            ),
          },
        ],
      ];

      const pad = 6;
      const cols = [
        margin + pad,
        margin + contentWidth * 0.35,
        margin + contentWidth * 0.67,
      ];
      const rowYs = infoItems.map((_, index) => y + pad + index * 11);

      infoItems.forEach((row, ri) => {
        row.forEach((item, ci) => {
          doc.setFontSize(5.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(muted.r, muted.g, muted.b);
          doc.text(item.label, cols[ci], rowYs[ri]);
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(slate.r, slate.g, slate.b);
          doc.text(sanitizePdfText(item.value), cols[ci], rowYs[ri] + 4.2);
        });
      });

      // ════════════════════════════════════════
      // KPI CARDS — accent left border
      // ════════════════════════════════════════
      y += panelH + 8;
      const kpiGap = 4;
      const kpiW = (contentWidth - kpiGap * 3) / 4;
      const kpiH = 24;

      if (y + kpiH > pageHeight - 18) {
        doc.addPage();
        y = 18;
      }

      const totalDiff = totalDiffForTarget;
      const perKgDiff =
        summaryStatistics.mainClient.totalKilos > 0
          ? totalDiff / summaryStatistics.mainClient.totalKilos
          : 0;
      const compPct = summaryStatistics.comparablePercentage ?? 0;
      const gPctVsTarget = globalMetrics?.pctVsTarget;
      const gQuartile = globalMetrics?.quartileDist;
      const aboveTargetPct = gQuartile ? kilosAboveTarget(gQuartile) : null;

      const kpis = [
        {
          label: `TOTAL ${priceType} DIFF vs ${targetLabel.toUpperCase()}`,
          value: formatMoneyUSD(totalDiff, { decimals: 0 }),
          accent: totalDiff >= 0 ? green : red,
        },
        {
          label: `${priceType}/KG DIFF vs ${targetLabel.toUpperCase()}`,
          value: formatMoneyUSD(perKgDiff, { decimals: 2 }),
          accent: perKgDiff >= 0 ? green : red,
        },
        {
          label: `% VS ${targetLabel.toUpperCase()} (POND.)`,
          value:
            gPctVsTarget != null
              ? `${gPctVsTarget >= 0 ? '+' : ''}${(gPctVsTarget * 100).toFixed(1)}%`
              : '-',
          accent: gPctVsTarget != null && gPctVsTarget >= 0 ? green : red,
        },
        {
          label: `KG SOBRE ${targetLabel.toUpperCase()}`,
          value: aboveTargetPct != null ? `${aboveTargetPct}%` : '-',
          accent: aboveTargetPct != null && aboveTargetPct >= 50 ? green : red,
        },
      ];

      kpis.forEach((kpi, i) => {
        const kx = margin + i * (kpiW + kpiGap);

        // Card
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(borderC.r, borderC.g, borderC.b);
        doc.setLineWidth(0.2);
        doc.roundedRect(kx, y, kpiW, kpiH, 2, 2, 'FD');

        // Left accent
        doc.setFillColor(kpi.accent.r, kpi.accent.g, kpi.accent.b);
        doc.roundedRect(kx, y, 2.2, kpiH, 2, 0, 'F');
        doc.rect(kx + 1.2, y, 1.2, kpiH, 'F');

        // Label
        doc.setFontSize(5.2);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(muted.r, muted.g, muted.b);
        doc.text(kpi.label, kx + 7, y + 7);

        // Value
        doc.setFontSize(15);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(kpi.accent.r, kpi.accent.g, kpi.accent.b);
        doc.text(kpi.value, kx + 7, y + 17.5);
      });

      // ════════════════════════════════════════
      // BREAKDOWN TABLES
      // ════════════════════════════════════════
      y += kpiH + 10;
      const breakdownMargin = margin;
      const sectionTitleStyle = (doc: jsPDF, label: string, yPos: number) => {
        doc.setFillColor(cherry.r, cherry.g, cherry.b);
        doc.rect(breakdownMargin, yPos - 3, 2.5, 2.5, 'F');
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(charcoal.r, charcoal.g, charcoal.b);
        doc.text(label, breakdownMargin + 5.5, yPos);
      };
      const ensureSectionSpace = (yPos: number, minimumHeight = 28) => {
        if (yPos + minimumHeight > pageHeight - 18) {
          doc.addPage();
          return 18;
        }
        return yPos;
      };

      const colorDiffCell = (data: any, colIdx: number) => {
        if (data.section === 'body' && data.column.index === colIdx) {
          const raw = data.cell.raw as string;
          if (raw && raw !== '-') {
            const isNeg = raw.includes('-');
            data.cell.styles.textColor = isNeg
              ? [red.r, red.g, red.b]
              : [green.r, green.g, green.b];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      };

      const colorPercentileCell = (data: any, colIdx: number) => {
        if (data.section === 'body' && data.column.index === colIdx) {
          const raw = data.cell.raw as string;
          if (raw && raw !== '-') {
            const val = parseInt(raw);
            if (!isNaN(val)) {
              data.cell.styles.textColor =
                val >= 50 ? [green.r, green.g, green.b] : [red.r, red.g, red.b];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      };

      const miniTableStyles = {
        styles: {
          fontSize: 7,
          cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
          lineColor: [borderC.r, borderC.g, borderC.b],
          lineWidth: 0.12,
          textColor: [slate.r, slate.g, slate.b],
        },
        headStyles: {
          fillColor: [charcoal.r, charcoal.g, charcoal.b],
          textColor: [255, 255, 255],
          fontStyle: 'bold' as const,
          fontSize: 6.5,
          cellPadding: { top: 2.5, right: 2, bottom: 2.5, left: 2 },
        },
        alternateRowStyles: { fillColor: [faintBg.r, faintBg.g, faintBg.b] },
      };

      // --- By Ship ---
      const shipBreakdown: Record<
        string,
        { kilos: number; othersKilos: number; diff: number; groupKey: string }
      > = {};
      Object.entries(shipmentData.byShips).forEach(([key, group]) => {
        const label = formatGroupLabel(key);
        if (!shipBreakdown[label])
          shipBreakdown[label] = {
            kilos: 0,
            othersKilos: 0,
            diff: 0,
            groupKey: key,
          };
        Object.entries(group.mainClient.details).forEach(([dk, md]) => {
          shipBreakdown[label].kilos += md.kilos;
          const ind = group.comparisons['INDUSTRIA']?.details[dk];
          if (ind) {
            shipBreakdown[label].othersKilos += ind.kilos;
            shipBreakdown[label].diff +=
              (md.fobPerKg - ind.fobPerKg) * md.kilos;
          }
        });
      });

      // Helper to build quartile cells for PDF rows
      const quartileCells = (qd: QuartileDistribution | undefined) => {
        if (!qd) return ['-', '-', '-', '-', '-'];
        const abv = kilosAboveTarget(qd);
        const q1 = Math.round((qd.q1Kilos / qd.totalKilos) * 100);
        const q2 = Math.round((qd.q2Kilos / qd.totalKilos) * 100);
        const q3 = Math.round((qd.q3Kilos / qd.totalKilos) * 100);
        const q4 = Math.round((qd.q4Kilos / qd.totalKilos) * 100);
        return [`${abv}%`, `${q1}%`, `${q2}%`, `${q3}%`, `${q4}%`];
      };

      const pdfTableHead = (label: string) => [
        [
          label,
          'Own KG',
          'Ind. KG',
          'Total Diff.',
          'Diff. /KG',
          `% vs ${targetLabel}`,
          `KG > ${targetLabel}`,
          'Q1',
          'Q2',
          'Q3',
          'Q4',
        ],
      ];
      const pdfTableColStyles = {
        1: { halign: 'right' as const },
        2: { halign: 'right' as const },
        3: { halign: 'right' as const },
        4: { halign: 'right' as const },
        5: { halign: 'right' as const },
        6: { halign: 'right' as const },
        7: { halign: 'right' as const },
        8: { halign: 'right' as const },
        9: { halign: 'right' as const },
        10: { halign: 'right' as const },
      };
      const pdfTableCellColor = (data: any) => {
        colorDiffCell(data, 3);
        colorDiffCell(data, 4);
        colorDiffCell(data, 5);
        colorPercentileCell(data, 6);
      };

      const dimensionRowsForPdf = (rows: IndustryDimensionRow[]) => {
        const totals = rows.reduce(
          (accumulator, row) => ({
            kilos: accumulator.kilos + row.kilos,
            othersKilos: accumulator.othersKilos + row.othersKilos,
            difference: accumulator.difference + row.totalDifference,
          }),
          { kilos: 0, othersKilos: 0, difference: 0 }
        );

        return [
          [
            'TOTAL',
            Math.round(totals.kilos).toLocaleString('es-CL'),
            Math.round(totals.othersKilos).toLocaleString('es-CL'),
            formatMoneyUSD(totals.difference, { decimals: 0 }),
            totals.kilos > 0
              ? formatMoneyUSD(totals.difference / totals.kilos, {
                  decimals: 2,
                })
              : '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
          ],
          ...[...rows]
            .sort((a, b) => b.totalDifference - a.totalDifference)
            .map((row) => [
              row.key,
              Math.round(row.kilos).toLocaleString('es-CL'),
              Math.round(row.othersKilos).toLocaleString('es-CL'),
              formatMoneyUSD(row.totalDifference, { decimals: 0 }),
              row.kilos > 0
                ? formatMoneyUSD(row.totalDifference / row.kilos, {
                    decimals: 2,
                  })
                : '-',
              row.pctVsTarget != null
                ? `${row.pctVsTarget >= 0 ? '+' : ''}${(
                    row.pctVsTarget * 100
                  ).toFixed(1)}%`
                : '-',
              row.kilosAboveTargetPct != null
                ? `${row.kilosAboveTargetPct}%`
                : '-',
              row.q1Pct != null ? `${row.q1Pct}%` : '-',
              row.q2Pct != null ? `${row.q2Pct}%` : '-',
              row.q3Pct != null ? `${row.q3Pct}%` : '-',
              row.q4Pct != null ? `${row.q4Pct}%` : '-',
            ]),
        ];
      };

      const dimensionTableCellColor = (data: any) => {
        pdfTableCellColor(data);
        if (data.section === 'body' && data.row.index === 0) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [faintBg.r, faintBg.g, faintBg.b];
        }
      };

      // --- Global Quartile Distribution ---
      if (globalMetrics?.quartileDist) {
        y = ensureSectionSpace(y);
        const gqd = globalMetrics.quartileDist;
        const gq1 = Math.round((gqd.q1Kilos / gqd.totalKilos) * 100);
        const gq2 = Math.round((gqd.q2Kilos / gqd.totalKilos) * 100);
        const gq3 = Math.round((gqd.q3Kilos / gqd.totalKilos) * 100);
        const gq4 = Math.round((gqd.q4Kilos / gqd.totalKilos) * 100);
        sectionTitleStyle(
          doc,
          `Global Quartile Distribution (KG > ${targetLabel}: ${kilosAboveTarget(gqd)}%)`,
          y
        );
        y += 4;
        autoTable(doc, {
          startY: y,
          margin: { left: breakdownMargin, right: breakdownMargin, bottom: 18 },
          head: [['Q1', 'Q2', 'Q3', 'Q4']],
          body: [[`${gq1}%`, `${gq2}%`, `${gq3}%`, `${gq4}%`]],
          ...miniTableStyles,
          columnStyles: {
            0: { halign: 'right' },
            1: { halign: 'right' },
            2: { halign: 'right' },
            3: { halign: 'right' },
          },
        });
        y = (doc as any).lastAutoTable?.finalY + 10 || y + 20;
      }

      // --- By Client ---
      let bY = ensureSectionSpace(y);
      sectionTitleStyle(doc, 'Breakdown by Client', bY);
      bY += 4;

      autoTable(doc, {
        startY: bY,
        margin: { left: breakdownMargin, right: breakdownMargin, bottom: 18 },
        head: pdfTableHead('Client'),
        body: dimensionRowsForPdf(clientAnalysisRows),
        ...miniTableStyles,
        columnStyles: pdfTableColStyles,
        didParseCell: dimensionTableCellColor,
      });

      // --- By Destination Country ---
      bY = (doc as any).lastAutoTable?.finalY + 12 || 200;
      bY = ensureSectionSpace(bY);
      sectionTitleStyle(doc, 'Breakdown by Destination Country', bY);
      bY += 4;

      autoTable(doc, {
        startY: bY,
        margin: { left: breakdownMargin, right: breakdownMargin, bottom: 18 },
        head: pdfTableHead('Destination Country'),
        body: dimensionRowsForPdf(destinationCountryAnalysisRows),
        ...miniTableStyles,
        columnStyles: pdfTableColStyles,
        didParseCell: dimensionTableCellColor,
      });

      // --- By Ship ---
      bY = (doc as any).lastAutoTable?.finalY + 12 || 200;
      bY = ensureSectionSpace(bY);
      sectionTitleStyle(doc, 'Breakdown by Shipment', bY);
      bY += 4;

      const shipRows = Object.entries(shipBreakdown).map(([name, d]) => {
        const score = metricsByShip?.scores?.[d.groupKey];
        const qd = metricsByShip?.quartiles?.[d.groupKey];
        return [
          name,
          Math.round(d.kilos).toLocaleString('es-CL'),
          Math.round(d.othersKilos).toLocaleString('es-CL'),
          formatMoneyUSD(d.diff, { decimals: 0 }),
          d.kilos > 0 ? formatMoneyUSD(d.diff / d.kilos, { decimals: 2 }) : '-',
          score != null
            ? `${score >= 0 ? '+' : ''}${(score * 100).toFixed(1)}%`
            : '-',
          ...quartileCells(qd),
        ];
      });

      autoTable(doc, {
        startY: bY,
        margin: { left: breakdownMargin, right: breakdownMargin, bottom: 18 },
        head: pdfTableHead('Shipment'),
        body: shipRows,
        ...miniTableStyles,
        columnStyles: pdfTableColStyles,
        didParseCell: pdfTableCellColor,
      });

      // --- By Variety ---
      const varietyBreakdown: Record<
        string,
        { kilos: number; othersKilos: number; diff: number }
      > = {};
      Object.values(shipmentData.byShips).forEach((group) => {
        Object.entries(group.mainClient.details).forEach(([dk, md]) => {
          const variety = dk.split('-')[0];
          if (!varietyBreakdown[variety])
            varietyBreakdown[variety] = { kilos: 0, othersKilos: 0, diff: 0 };
          varietyBreakdown[variety].kilos += md.kilos;
          const ind = group.comparisons['INDUSTRIA']?.details[dk];
          if (ind) {
            varietyBreakdown[variety].othersKilos += ind.kilos;
            varietyBreakdown[variety].diff +=
              (md.fobPerKg - ind.fobPerKg) * md.kilos;
          }
        });
      });

      bY = (doc as any).lastAutoTable?.finalY + 12 || 200;
      bY = ensureSectionSpace(bY);
      sectionTitleStyle(doc, 'Breakdown by Variety', bY);
      bY += 4;

      const varietyRows = Object.entries(varietyBreakdown)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, d]) => {
          const score = metricsByVariety?.scores?.[name];
          const qd = metricsByVariety?.quartiles?.[name];
          return [
            name,
            Math.round(d.kilos).toLocaleString('es-CL'),
            Math.round(d.othersKilos).toLocaleString('es-CL'),
            formatMoneyUSD(d.diff, { decimals: 0 }),
            d.kilos > 0
              ? formatMoneyUSD(d.diff / d.kilos, { decimals: 2 })
              : '-',
            score != null
              ? `${score >= 0 ? '+' : ''}${(score * 100).toFixed(1)}%`
              : '-',
            ...quartileCells(qd),
          ];
        });

      autoTable(doc, {
        startY: bY,
        margin: { left: breakdownMargin, right: breakdownMargin, bottom: 18 },
        head: pdfTableHead('Variety'),
        body: varietyRows,
        ...miniTableStyles,
        columnStyles: pdfTableColStyles,
        didParseCell: pdfTableCellColor,
      });

      // --- By Caliber ---
      const caliberOrder = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
      const caliberBreakdown: Record<
        string,
        { kilos: number; othersKilos: number; diff: number }
      > = {};
      Object.values(shipmentData.byShips).forEach((group) => {
        Object.entries(group.mainClient.details).forEach(([dk, md]) => {
          const caliber = dk.split('-')[1];
          if (!caliberBreakdown[caliber])
            caliberBreakdown[caliber] = { kilos: 0, othersKilos: 0, diff: 0 };
          caliberBreakdown[caliber].kilos += md.kilos;
          const ind = group.comparisons['INDUSTRIA']?.details[dk];
          if (ind) {
            caliberBreakdown[caliber].othersKilos += ind.kilos;
            caliberBreakdown[caliber].diff +=
              (md.fobPerKg - ind.fobPerKg) * md.kilos;
          }
        });
      });

      bY = (doc as any).lastAutoTable?.finalY + 12 || 200;
      bY = ensureSectionSpace(bY);
      sectionTitleStyle(doc, 'Breakdown by Size', bY);
      bY += 4;

      const caliberRows = Object.entries(caliberBreakdown)
        .sort(([a], [b]) => {
          const ia = caliberOrder.indexOf(a);
          const ib = caliberOrder.indexOf(b);
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        })
        .map(([name, d]) => {
          const score = metricsByCaliber?.scores?.[name];
          const qd = metricsByCaliber?.quartiles?.[name];
          return [
            name,
            Math.round(d.kilos).toLocaleString('es-CL'),
            Math.round(d.othersKilos).toLocaleString('es-CL'),
            formatMoneyUSD(d.diff, { decimals: 0 }),
            d.kilos > 0
              ? formatMoneyUSD(d.diff / d.kilos, { decimals: 2 })
              : '-',
            score != null
              ? `${score >= 0 ? '+' : ''}${(score * 100).toFixed(1)}%`
              : '-',
            ...quartileCells(qd),
          ];
        });

      autoTable(doc, {
        startY: bY,
        margin: { left: breakdownMargin, right: breakdownMargin, bottom: 18 },
        head: pdfTableHead('Size'),
        body: caliberRows,
        ...miniTableStyles,
        columnStyles: pdfTableColStyles,
        didParseCell: pdfTableCellColor,
      });

      // ════════════════════════════════════════
      // FOOTER — applied to every page
      // ════════════════════════════════════════
      drawFooter();

      const clientLabel =
        selectedClients.includes(ALL_CLIENTS) &&
        selectedResultClients.length === availableClients.length
          ? 'Todos'
          : selectedResultClients.join('_') || 'Sin_clientes';
      const fileName = `Certificado_Industria_${clientLabel.replace(/\s+/g, '_')}_${selectedSeason || ''}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  const handleSaveClaim = async () => {
    if (!summaryStatistics || selectedClients.length === 0) return;

    try {
      setIsSavingClaim(true);

      const clientSettlements = selectedClients.includes(ALL_CLIENTS)
        ? settlements.filter(
            (settlement) =>
              !!settlement.receiver &&
              selectedResultClients.includes(settlement.receiver)
          )
        : settlements.filter(
            (settlement) =>
              !!settlement.receiver &&
              selectedClients.includes(settlement.receiver) &&
              selectedResultClients.includes(settlement.receiver)
          );

      if (clientSettlements.length === 0) {
        toast.error('Genera el comparativo primero');
        return;
      }

      const mainSettlement = clientSettlements[0];

      const productDefinition = 'VARIETY-CALIBER';

      const targetLabel =
        comparisonTarget === 'median'
          ? 'Mediana'
          : comparisonTarget === 'q1'
            ? 'Q1'
            : 'Q3';
      const claimSourceDetails = mainClientDetails.filter(
        (detail: MainClientDetailWithComparable) =>
          detail.hasComparison &&
          !!detail.client &&
          selectedResultClients.includes(detail.client) &&
          !!detail.destinationCountry &&
          selectedDestinationCountries.includes(detail.destinationCountry) &&
          !!detail.shipTripId &&
          selectedShipTrips.includes(detail.shipTripId) &&
          !!detail.variety &&
          selectedVarieties.includes(detail.variety) &&
          !!detail.caliber &&
          selectedCalibers.includes(detail.caliber)
      );

      if (claimSourceDetails.length === 0) {
        toast.error('No hay detalles comparables con los filtros seleccionados');
        return;
      }

      const diff = claimSourceDetails.reduce(
        (total, detail) =>
          total +
          (comparisonTarget === 'q1'
            ? detail.differenceQ1USD || 0
            : comparisonTarget === 'q3'
              ? detail.differenceQ3USD || 0
              : detail.differenceUSD || 0),
        0
      );

      // Build filter notes
      const filterParts: string[] = [`vs ${targetLabel}`];
      if (selectedResultClients.length < availableClients.length) {
        filterParts.push(`Clientes: ${selectedResultClients.join(', ')}`);
      }
      if (selectedVarieties.length < uniqueVarieties.length) {
        filterParts.push(`Variedades: ${selectedVarieties.join(', ')}`);
      }
      if (selectedCalibers.length < uniqueCalibers.length) {
        filterParts.push(`Calibres: ${selectedCalibers.join(', ')}`);
      }
      if (selectedShipTrips.length < uniqueShipTrips.length) {
        const tripLabels = uniqueShipTrips
          .filter((t) => selectedShipTrips.includes(t.id))
          .map((t) => t.label);
        filterParts.push(`Naves: ${tripLabels.join(', ')}`);
      }
      if (
        selectedDestinationCountries.length < uniqueDestinationCountries.length
      ) {
        filterParts.push(
          `Países de destino: ${selectedDestinationCountries.join(', ')}`
        );
      }

      // Claim header
      const claimRow = await createClaim({
        exporter_id: mainSettlement.exporter_id,
        receiver:
          selectedResultClients.length === 1
            ? selectedResultClients[0]
            : 'Múltiples clientes',
        season: mainSettlement.season,
        notes: `Reclamo auto – diff total ${formatMoneyUSD(diff, { decimals: 0 })} (${filterParts.join(' | ')})`,
        description: claimDescription || `Sin descripción`,
        total_claim_usd: -diff,
        currency: 'USD',
        comparison_type: 'industry',
        sinister_filter: sinisterFilter,
        shipment_type: 'M',
        incoterm: priceType,
        comparison_clients: [],
        product_definition: productDefinition,
        comparison_target: comparisonTarget,
        pool_methodology: methodology,
        include_revenues_main: includeRevenues,
        include_revenues_comparison: false,
        filtered_clients: selectedResultClients,
        filtered_destination_countries:
          selectedDestinationCountries.length <
          uniqueDestinationCountries.length
            ? selectedDestinationCountries
            : undefined,
        filtered_varieties:
          selectedVarieties.length < uniqueVarieties.length
            ? selectedVarieties
            : undefined,
        filtered_calibers:
          selectedCalibers.length < uniqueCalibers.length
            ? selectedCalibers
            : undefined,
        filtered_ship_trips:
          selectedShipTrips.length < uniqueShipTrips.length
            ? selectedShipTrips
            : undefined,
      });

      // Create claim details — use the target-specific comparable value
      const claimDetails = claimSourceDetails
        .map((detail: MainClientDetailWithComparable) => {
          const comparableFobPerKg =
            comparisonTarget === 'q1'
              ? detail.comparisonQ1FobPerKg || 0
              : comparisonTarget === 'q3'
                ? detail.comparisonQ3FobPerKg || 0
                : detail.comparisonFobPerKg || 0;
          return {
            claim_id: claimRow.id,
            settlement_detail_id: detail.settlement_detail_id,
            comparable_fob_per_kg: comparableFobPerKg,
            comparable_kilos: detail.comparableKilos,
            own_value_per_kg: detail.fobPerKg,
          };
        });

      await createClaimDetails(claimDetails);

      toast.success(
        `Comparativo #${claimRow.id} guardado. Revísalo en el menú Liquidaciones Clientes -> Compensaciones`
      );
      setShowClaimModal(false);
      setClaimDescription('');
    } catch (err) {
      console.error('Error saving claim:', err);
      toast.error('Error al guardar la compensación');
    } finally {
      setIsSavingClaim(false);
    }
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <PageLayout>
        <PageHeader
          title={t('Comparativo Industria')}
          description={t(
            'Compara el rendimiento de un cliente contra la mediana de la industria por producto (Variedad-Calibre) y nave.'
          )}
          titleAddon={
            <InformationTooltip>
              <div className='p-2'>
                <h3 className='font-semibold mb-2'>
                  {t('Descripción General:')}
                </h3>
                <p>
                  {t(
                    'Esta pantalla permite comparar los resultados de un cliente específico contra la mediana de la industria (todos los demás exportadores). Para cada producto (variedad-calibre) enviado al cliente seleccionado, muestra la diferencia entre lo que recibió ese cliente y la mediana de la industria para el mismo producto en la misma nave.'
                  )}
                </p>
              </div>
            </InformationTooltip>
          }
        />

        <div className='space-y-6'>
          {/* Card 1: Base Configuration */}
          <Card className='p-4'>
            <CardBody>
              <div className='flex items-center gap-2 mb-4'>
                <div>
                  <h2 className='text-lg font-semibold'>
                    {t('1. Configuración Base')}
                  </h2>
                  <p className='text-default-500 text-sm'>
                    {t('Selecciona los filtros base para el análisis.')}
                  </p>
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='space-y-4'>
                  <h3 className='text-sm font-medium text-default-600'>
                    {t('Filtros')}
                  </h3>
                  <Select
                    label={t('Filtro de Siniestros')}
                    selectedKeys={[sinisterFilter]}
                    size='sm'
                    items={[
                      { key: 'all', value: 'all', textValue: t('Todos') },
                      {
                        key: 'sinister',
                        value: 'sinister',
                        textValue: t('Solo Siniestros'),
                      },
                      {
                        key: 'non_sinister',
                        value: 'non_sinister',
                        textValue: t('Sin Siniestros'),
                      },
                    ]}
                    onSelectionChange={(keys) =>
                      setSinisterFilter(
                        Array.from(keys)[0] as typeof sinisterFilter
                      )
                    }
                  >
                    {(item) => (
                      <SelectItem
                        key={item.key}
                        value={item.value}
                        textValue={item.textValue}
                      >
                        {item.textValue}
                      </SelectItem>
                    )}
                  </Select>
                </div>
                <div className='space-y-4'>
                  <h3 className='text-sm font-medium text-default-600'>
                    {t('Incoterm')}
                  </h3>
                  <Select
                    label={t('Incoterm')}
                    selectedKeys={[priceType]}
                    size='sm'
                    items={[
                      {
                        key: 'FOB',
                        value: 'FOB',
                        textValue: 'FOB',
                        description: t(
                          'FOB considera diferencias de flete dentro de la comparación.'
                        ),
                      },
                      {
                        key: 'CIF',
                        value: 'CIF',
                        textValue: 'CIF',
                        description: t(
                          'CIF excluye el efecto por diferencias en las tarifas de flete.'
                        ),
                      },
                    ]}
                    onSelectionChange={(keys) =>
                      setPriceType(Array.from(keys)[0] as typeof priceType)
                    }
                  >
                    {(item) => (
                      <SelectItem
                        key={item.key}
                        value={item.value}
                        textValue={item.textValue}
                        description={item.description}
                      >
                        {item.textValue}
                      </SelectItem>
                    )}
                  </Select>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Card 2: Client Selection */}
          <Card className='p-4'>
            <CardBody>
              <div className='flex items-center gap-2 mb-4'>
                <div>
                  <h2 className='text-lg font-semibold'>{t('2. Cliente')}</h2>
                  <p className='text-default-500 text-sm'>
                    {t('Selecciona el cliente a comparar contra la industria.')}
                  </p>
                </div>
              </div>

              <div className='space-y-4'>
                {isLoadingClients ? (
                  <LoadingState label={t('Cargando clientes...')} />
                ) : availableClients.length === 0 ? (
                  <Alert variant='warning' showIcon>
                    {t(
                      'No se encontraron liquidaciones marítimas aprobadas para esta temporada. El comparativo industria solo considera embarques marítimos.'
                    )}
                  </Alert>
                ) : (
                  <Select
                    label={t('Clientes')}
                    placeholder={t('Selecciona uno o más clientes')}
                    selectionMode='multiple'
                    className='w-full'
                    size='sm'
                    selectedKeys={new Set(selectedClients)}
                    items={[
                      {
                        key: ALL_CLIENTS,
                        value: ALL_CLIENTS,
                        textValue: t('Todos los clientes'),
                      },
                      ...availableClients.map((client) => ({
                        key: client,
                        value: client,
                        textValue: client,
                      })),
                    ]}
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys as Set<string>);
                      // If "All" is newly selected, select only "All"
                      if (
                        selected.includes(ALL_CLIENTS) &&
                        !selectedClients.includes(ALL_CLIENTS)
                      ) {
                        setSelectedClients([ALL_CLIENTS]);
                      } else if (
                        selected.includes(ALL_CLIENTS) &&
                        selected.length > 1
                      ) {
                        // If individual clients selected while "All" was active, deselect "All"
                        setSelectedClients(
                          selected.filter((k) => k !== ALL_CLIENTS)
                        );
                      } else {
                        setSelectedClients(selected);
                      }
                      setSummaryStatistics(null);
                      setShipmentData(null);
                    }}
                  >
                    {(item) => (
                      <SelectItem
                        key={item.key}
                        value={item.value}
                        textValue={item.textValue}
                      >
                        {item.textValue}
                      </SelectItem>
                    )}
                  </Select>
                )}
                <Select
                  label={t('Ponderación')}
                  selectedKeys={[methodology]}
                  onSelectionChange={(keys) =>
                    setMethodology(
                      Array.from(keys)[0] as 'by_volume' | 'by_exporter'
                    )
                  }
                  className='w-full'
                  size='sm'
                >
                  <SelectItem
                    key='by_volume'
                    value='by_volume'
                    description={t(
                      'Cada exportadora contribuye con sus kilos al cálculo final de los cuartiles. Esto significa que las exportadoras con mayor volumen de ventas tendrán un mayor impacto en las estadísticas finales.'
                    )}
                  >
                    {t('Por Volumen')}
                  </SelectItem>
                  <SelectItem
                    key='by_exporter'
                    value='by_exporter'
                    description={t(
                      'Se calcula el FOB/CIF promedio de cada exportadora y luego para generar los cuartiles se da igual peso a cada exportadora, independientemente de su volumen de ventas.'
                    )}
                  >
                    {t('Por Exportadora')}
                  </SelectItem>
                </Select>
                {selectedClients.length > 0 && (
                  <div className='flex items-center gap-3 mt-2'>
                    <Switch
                      size='sm'
                      isSelected={includeRevenues}
                      onValueChange={setIncludeRevenues}
                    >
                      <span className='text-sm'>
                        {t('Considerar ingresos adicionales')}
                      </span>
                    </Switch>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Card 3: Comparable product definition */}
          {selectedClients.length > 0 && (
            <Card className='p-4'>
              <CardBody>
                <div className='flex items-center gap-2 mb-4'>
                  <div>
                    <h2 className='text-lg font-semibold'>
                      {t('3. Producto a Comparar')}
                    </h2>
                    <p className='text-default-500 text-sm'>
                      {t('Los productos se comparan por variedad y calibre.')}
                    </p>
                  </div>
                </div>
                <div className='space-y-3'>
                  <div className='flex items-center gap-6'>
                    <Checkbox isSelected isDisabled size='sm'>
                      {t('Variedad')}
                    </Checkbox>
                    <Checkbox isSelected isDisabled size='sm'>
                      {t('Calibre')}
                    </Checkbox>
                  </div>
                  <div className='flex items-center gap-2 p-3 bg-default-100 dark:bg-default-50/10 rounded-lg'>
                    <Icon
                      icon='tabler:info-circle'
                      className='text-default-400'
                      width={18}
                    />
                    <p className='text-sm text-default-500'>
                      {t(
                        'Solo embarques marítimos. Agrupados por viaje de nave.'
                      )}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Card 4: Analysis parameters */}
          {selectedClients.length > 0 && (
            <Card className='p-4'>
              <CardBody>
                <div className='flex items-center gap-2 mb-4'>
                  <div>
                    <h2 className='text-lg font-semibold'>
                      {t('4. Parámetros')}
                    </h2>
                  </div>
                </div>

                <div className='space-y-4'>
                  <div className='max-w-xl'>
                    <Select
                      label={t('Comparar con')}
                      selectedKeys={[comparisonTarget]}
                      onSelectionChange={(keys) =>
                        setComparisonTarget(
                          Array.from(keys)[0] as 'median' | 'q1' | 'q3'
                        )
                      }
                      size='sm'
                    >
                      <SelectItem
                        key='median'
                        value='median'
                        textValue={t('Mediana (Percentil 50)')}
                      >
                        {t('Mediana (Percentil 50)')}
                      </SelectItem>
                      <SelectItem
                        key='q1'
                        value='q1'
                        textValue={`${t('Q1 (Percentil 25)')} - ${t('Menos Exigente')}`}
                      >
                        <div className='flex items-center gap-2'>
                          <span>{t('Q1 (Percentil 25)')}</span>
                          <span className='px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'>
                            {t('Menos Exigente')}
                          </span>
                        </div>
                      </SelectItem>
                      <SelectItem
                        key='q3'
                        value='q3'
                        textValue={`${t('Q3 (Percentil 75)')} - ${t('Más Exigente')}`}
                      >
                        <div className='flex items-center gap-2'>
                          <span>{t('Q3 (Percentil 75)')}</span>
                          <span className='px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'>
                            {t('Más Exigente')}
                          </span>
                        </div>
                      </SelectItem>
                    </Select>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Card 5: Actions */}
          {selectedClients.length > 0 && (
            <div ref={actionsCardRef}>
              <Card className='p-4'>
                <CardBody>
                  <div className='flex flex-col md:flex-row justify-between items-center gap-4'>
                    <div className='flex-1'>
                      <h3 className='text-lg font-semibold text-default-700'>
                        {t('5. Acciones')}
                      </h3>
                      <p className='text-sm text-default-500'>
                        {t('Genera el comparativo y exporta los resultados')}
                      </p>
                    </div>
                    <div className='flex flex-col md:flex-row items-center w-full md:w-auto justify-center md:justify-end gap-3'>
                      {summaryStatistics && !isOptionsChanged && (
                        <>
                          <Button
                            variant='flat'
                            onClick={exportToPDF}
                            size='lg'
                            className='transition-all w-full md:w-auto'
                            startContent={
                              <Icon
                                icon='mdi:file-pdf-box'
                                className='text-xl'
                              />
                            }
                          >
                            {t('Exportar PDF')}
                          </Button>
                          {summaryStatistics!.totalFobDifference < 0 && (
                            <>
                              <Button
                                variant='flat'
                                onClick={() => setShowClaimModal(true)}
                                size='lg'
                                className='transition-all w-full md:w-auto'
                                startContent={
                                  <Icon
                                    icon='mdi:file-document-edit'
                                    className='text-xl'
                                  />
                                }
                              >
                                {t('Guardar Comparativo')}
                              </Button>
                              {showClaimModal && (
                                <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
                                  <div className='bg-background rounded-lg p-6 w-full max-w-md'>
                                    <h3 className='text-lg font-semibold mb-4'>
                                      {t('Guardar Comparativo')}
                                    </h3>
                                    <div className='mb-4'>
                                      <label className='block text-sm font-medium text-default-600 mb-2'>
                                        {t('Descripción (opcional)')}
                                      </label>
                                      <textarea
                                        className='w-full p-2 border border-divider rounded-md'
                                        rows={3}
                                        placeholder={t(
                                          'Ingresa una descripción para este comparativo...'
                                        )}
                                        value={claimDescription}
                                        onChange={(e) =>
                                          setClaimDescription(e.target.value)
                                        }
                                      />
                                    </div>
                                    <div className='flex justify-end gap-3'>
                                      <Button
                                        variant='light'
                                        onClick={() => {
                                          setShowClaimModal(false);
                                          setClaimDescription('');
                                        }}
                                      >
                                        {t('Cancelar')}
                                      </Button>
                                      <Button
                                        color='primary'
                                        onClick={handleSaveClaim}
                                        isLoading={isSavingClaim}
                                      >
                                        {t('Guardar')}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                      <Button
                        color='primary'
                        onClick={handleGenerateComparison}
                        isDisabled={!isComparisonReady}
                        isLoading={isLoading}
                        size='lg'
                        startContent={
                          <Icon icon='mdi:chart-box' className='text-xl' />
                        }
                      >
                        {t('Generar Comparativo')}
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          )}

          <Divider className='my-6' />

          {/* Results Section */}
          {summaryStatistics && (
            <>
              {isOptionsChanged && (
                <Alert variant='warning' className='mb-4' showIcon>
                  {t(
                    'Has realizado cambios en las opciones. Por favor, presiona "Generar Comparativo" para actualizar los resultados.'
                  )}
                </Alert>
              )}
              <div ref={resultsRef} className='flex items-center gap-2'>
                <p className='text-2xl font-semibold'>
                  {t('Resultados del Análisis')}
                </p>
                {/* <InformationTooltip>
                  <div className='p-2'>
                    <h3 className='font-semibold mb-2'>
                      {t('Resultados del Análisis:')}
                    </h3>
                    <ul className='list-disc pl-4'>
                      <li>
                        <strong>{t('Diferencia Total:')}</strong>{' '}
                        {t(
                          'Muestra la diferencia total entre el cliente y la mediana de la industria.'
                        )}
                      </li>
                      <li>
                        <strong>{t('Diferencia por Kilo:')}</strong>{' '}
                        {t(
                          'Muestra la diferencia por kilo entre el cliente y la mediana de la industria.'
                        )}
                      </li>
                      <li>
                        <strong>{t('Por Variedad:')}</strong>{' '}
                        {t(
                          'Tabla que muestra la comparación por variedad de productos.'
                        )}
                      </li>
                    </ul>
                  </div>
                </InformationTooltip> */}
              </div>

              <Card className='p-4 my-6'>
                <CardBody>
                  <div className='flex items-center gap-2 mb-4'>
                    <div>
                      <h2 className='text-lg font-semibold'>{t('Filtros')}</h2>
                      <p className='text-default-500 text-sm'>
                        {t(
                          'Ajusta estos filtros para refinar los resultados sin necesidad de regenerar.'
                        )}
                      </p>
                    </div>
                  </div>
                  <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'>
                    <MultiSelectWithActions
                      label={t('Clientes')}
                      placeholder={t('Selecciona clientes')}
                      options={resultClientOptions.map((client) => ({
                        value: client,
                        label: client,
                      }))}
                      selectedKeys={selectedResultClients}
                      onSelectionChange={setSelectedResultClients}
                      size='sm'
                      isDisabled={resultClientOptions.length === 0}
                    />
                    <MultiSelectWithActions
                      label={t('Países de Destino')}
                      placeholder={t('Selecciona países de destino')}
                      options={uniqueDestinationCountries.map((country) => ({
                        value: country,
                        label: country,
                      }))}
                      selectedKeys={selectedDestinationCountries}
                      onSelectionChange={setSelectedDestinationCountries}
                      size='sm'
                      isDisabled={uniqueDestinationCountries.length === 0}
                    />
                    <MultiSelectWithActions
                      label={t('Naves')}
                      placeholder={t('Selecciona naves')}
                      options={uniqueShipTrips.map((trip) => ({
                        value: trip.id,
                        label: trip.label,
                      }))}
                      selectedKeys={selectedShipTrips}
                      onSelectionChange={setSelectedShipTrips}
                      size='sm'
                      isDisabled={uniqueShipTrips.length === 0}
                    />
                    <MultiSelectWithActions
                      label={t('Variedades')}
                      placeholder={t('Selecciona variedades')}
                      options={uniqueVarieties.map((variety) => ({
                        value: variety,
                        label: variety,
                      }))}
                      selectedKeys={selectedVarieties}
                      onSelectionChange={setSelectedVarieties}
                      size='sm'
                      isDisabled={uniqueVarieties.length === 0}
                    />
                    <MultiSelectWithActions
                      label={t('Calibres')}
                      placeholder={t('Selecciona calibres')}
                      options={uniqueCalibers.map((caliber) => ({
                        value: caliber,
                        label: caliber,
                      }))}
                      selectedKeys={selectedCalibers}
                      onSelectionChange={setSelectedCalibers}
                      size='sm'
                      isDisabled={uniqueCalibers.length === 0}
                    />
                  </div>
                </CardBody>
              </Card>

              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 my-6 items-center'>
                {/* KPI: Diff vs selected target */}
                <Card className='w-full min-h-52'>
                  <CardBody className='text-center flex flex-col justify-center items-center p-4'>
                    <Icon
                      icon='mdi:trending-up'
                      width={40}
                      height={40}
                      className='mb-4'
                    />
                    <p
                      className={`text-2xl font-semibold ${
                        totalDiffForTarget >= 0
                          ? 'text-success-500'
                          : 'text-danger-500'
                      } mb-2`}
                    >
                      {formatMoneyUSD(totalDiffForTarget, { decimals: 0 })}
                    </p>
                    <p className='text-sm text-default-500'>
                      {t(
                        `Diferencia Total ${priceType} vs ${comparisonTarget === 'median' ? 'Mediana' : comparisonTarget === 'q1' ? 'Q1' : 'Q3'}`
                      )}
                    </p>
                    {summaryStatistics.comparablePercentage !== undefined && (
                      <p className='text-sm text-default-400 mt-1'>
                        ({summaryStatistics.comparablePercentage}%{' '}
                        {t('comparable')})
                      </p>
                    )}
                    {diffContext && (
                      <div className='mt-4 pt-3 border-t border-default-200 w-full'>
                        <div className='flex flex-wrap justify-center gap-3'>
                          {diffContext.pctOfFob !== null && (
                            <div
                              className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                                totalDiffForTarget >= 0
                                  ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                  : 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                              }`}
                            >
                              {diffContext.pctOfFob > 0 ? '+' : ''}
                              {diffContext.pctOfFob.toFixed(1)}% {t('del')} FOB
                            </div>
                          )}
                          {diffContext.pctOfCommission !== null && (
                            <div
                              className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                                totalDiffForTarget >= 0
                                  ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                  : 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                              }`}
                            >
                              {diffContext.pctOfCommission > 0 ? '+' : ''}
                              {diffContext.pctOfCommission.toFixed(1)}%{' '}
                              {t('de Comisión')}
                            </div>
                          )}
                        </div>
                        <div className='flex items-center justify-center gap-2 mt-2'>
                          <span
                            className={`text-[10px] ${!diffContextComparableOnly ? 'text-default-500 font-medium' : 'text-default-400'}`}
                          >
                            {t('Sobre Total Liquidaciones')}
                          </span>
                          <Switch
                            size='sm'
                            isSelected={diffContextComparableOnly}
                            onValueChange={setDiffContextComparableOnly}
                          />
                          <span
                            className={`text-[10px] ${diffContextComparableOnly ? 'text-default-500 font-medium' : 'text-default-400'}`}
                          >
                            {t('Sobre Sólo Comparable')}
                          </span>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>

                {/* KPI: Diff per KG vs selected target */}
                <Card className='w-full min-h-52'>
                  <CardBody className='text-center flex flex-col justify-center items-center p-4'>
                    <Icon
                      icon='mdi:chart-line'
                      width={40}
                      height={40}
                      className='mb-4'
                    />
                    <p
                      className={`text-2xl font-semibold ${
                        (summaryStatistics.mainClient.totalKilos > 0
                          ? totalDiffForTarget /
                            summaryStatistics.mainClient.totalKilos
                          : 0) >= 0
                          ? 'text-success-500'
                          : 'text-danger-500'
                      } mb-2`}
                    >
                      {formatMoneyUSD(
                        summaryStatistics.mainClient.totalKilos > 0
                          ? totalDiffForTarget /
                              summaryStatistics.mainClient.totalKilos
                          : 0,
                        { decimals: 2 }
                      )}
                    </p>
                    <p className='text-sm text-default-500'>
                      {t(
                        `Diferencia ${priceType}/KG vs ${comparisonTarget === 'median' ? 'Mediana' : comparisonTarget === 'q1' ? 'Q1' : 'Q3'}`
                      )}
                    </p>
                  </CardBody>
                </Card>

                {/* KPI: % vs Target */}
                <Card className='w-full min-h-52'>
                  <CardBody className='text-center flex flex-col justify-center items-center p-4'>
                    <Icon
                      icon='mdi:percent'
                      width={40}
                      height={40}
                      className='mb-4'
                    />
                    <p
                      className={`text-2xl font-semibold ${
                        globalMetrics?.pctVsTarget != null &&
                        globalMetrics.pctVsTarget >= 0
                          ? 'text-success-500'
                          : 'text-danger-500'
                      } mb-2`}
                    >
                      {globalMetrics?.pctVsTarget != null
                        ? `${globalMetrics.pctVsTarget >= 0 ? '+' : ''}${(globalMetrics.pctVsTarget * 100).toFixed(1)}%`
                        : '-'}
                    </p>
                    <p className='text-sm text-default-500'>
                      {t(`% vs ${targetLabel}`)}
                    </p>
                    <p className='text-xs text-default-400 mt-1'>
                      {t('Ponderado por valor')} {priceType}
                    </p>
                  </CardBody>
                </Card>

                {/* KPI: Quartile Distribution */}
                <Card className='w-full min-h-52'>
                  <CardBody className='flex flex-col justify-center items-center p-4'>
                    {globalMetrics?.quartileDist ? (
                      (() => {
                        const qd = globalMetrics.quartileDist;
                        const aboveTarget = kilosAboveTarget(qd);
                        const q1Pct = Math.round(
                          (qd.q1Kilos / qd.totalKilos) * 100
                        );
                        const q2Pct = Math.round(
                          (qd.q2Kilos / qd.totalKilos) * 100
                        );
                        const q3Pct = Math.round(
                          (qd.q3Kilos / qd.totalKilos) * 100
                        );
                        const q4Pct = Math.round(
                          (qd.q4Kilos / qd.totalKilos) * 100
                        );
                        return (
                          <>
                            <p className='text-sm font-medium text-center text-default-600 mb-1'>
                              {aboveTarget}% {t(`KG sobre ${targetLabel}`)}
                            </p>
                            <ReactApexChart
                              type='bar'
                              width='100%'
                              height={100}
                              series={[
                                { name: 'Q1', data: [q1Pct] },
                                { name: 'Q2', data: [q2Pct] },
                                { name: 'Q3', data: [q3Pct] },
                                { name: 'Q4', data: [q4Pct] },
                              ]}
                              options={{
                                chart: {
                                  stacked: true,
                                  stackType: '100%',
                                  toolbar: { show: false },
                                  sparkline: { enabled: false },
                                },
                                plotOptions: {
                                  bar: { horizontal: true, barHeight: '60%' },
                                },
                                colors: [
                                  '#f31260',
                                  '#f5a524',
                                  '#17c964',
                                  '#006FEE',
                                ],
                                xaxis: {
                                  categories: [''],
                                  labels: { show: false },
                                  axisBorder: { show: false },
                                  axisTicks: { show: false },
                                },
                                yaxis: { labels: { show: false } },
                                grid: {
                                  show: false,
                                  padding: {
                                    top: -20,
                                    bottom: -10,
                                    left: 0,
                                    right: 0,
                                  },
                                },
                                legend: {
                                  position: 'bottom',
                                  fontSize: '11px',
                                  offsetY: -5,
                                  labels: {
                                    colors: darkMode ? '#a1a1aa' : '#52525b',
                                  },
                                },
                                dataLabels: {
                                  enabled: true,
                                  formatter: (val: number) =>
                                    val > 5 ? `${Math.round(val)}%` : '',
                                  style: { fontSize: '11px', colors: ['#fff'] },
                                },
                                tooltip: {
                                  y: {
                                    formatter: (val: number) =>
                                      `${val}% ${t('de los KG')}`,
                                  },
                                },
                              }}
                            />
                          </>
                        );
                      })()
                    ) : (
                      <p className='text-2xl font-semibold text-default-400'>
                        -
                      </p>
                    )}
                    <p className='text-sm text-default-500'>
                      {t('Distribución por Cuartil')}
                    </p>
                  </CardBody>
                </Card>
              </div>
            </>
          )}

          {/* Charts and Tables */}
          {shipmentData &&
            shipmentData.byShips &&
            Object.keys(shipmentData.byShips).length > 0 &&
            isComparisonReady && (
              <div className='space-y-6'>
                <div className='w-full'>
                  <IndustryDimensionAnalysis
                    title={t('Diferencia por Cliente')}
                    dimensionLabel={t('Cliente')}
                    rows={clientAnalysisRows}
                    comparisonTarget={comparisonTarget}
                  />
                </div>
                <div className='w-full'>
                  <IndustryDimensionAnalysis
                    title={t('Diferencia por País de Destino')}
                    dimensionLabel={t('País de Destino')}
                    rows={destinationCountryAnalysisRows}
                    comparisonTarget={comparisonTarget}
                  />
                </div>
                <div className='w-full'>
                  <ShipmentBarChart
                    data={shipmentData}
                    comparisonType='byShips'
                    pctVsMedianByKey={metricsByShip?.scores}
                    quartileDistByKey={metricsByShip?.quartiles}
                    comparisonTarget={comparisonTarget}
                  />
                </div>
                <div className='w-full'>
                  <VarietyComparisionBarChart
                    data={shipmentData}
                    comparisonType='byShips'
                    pctVsMedianByKey={metricsByVariety?.scores}
                    quartileDistByKey={metricsByVariety?.quartiles}
                    comparisonTarget={comparisonTarget}
                  />
                </div>
                <div className='w-full'>
                  <CaliberComparisionBarChart
                    data={shipmentData}
                    comparisonType='byShips'
                    pctVsMedianByKey={metricsByCaliber?.scores}
                    quartileDistByKey={metricsByCaliber?.quartiles}
                    comparisonTarget={comparisonTarget}
                  />
                </div>
              </div>
            )}
        </div>
      </PageLayout>
    </div>
  );
};

export default SettlementsComparativeIndustry;
